import type { JobKind } from "@unbox/api";
import { invalidateBlocklist } from "@/api/blocklist";
import { queryClient } from "@/providers/QueryClientProviderWrapper";

export type JobToast = {
  /** The bulk action whose labels, unit noun and results dialog this kind reuses. */
  actionType: string;
  invalidate?: (merchantId: string, workspaceId: string | null) => void;
};

/**
 * How each kind the API defines shows to the user who started it. `null` is a kind nobody starts
 * from the app, so nothing is shown even if a row reaches the page.
 */
export const JOB_TOASTS: Record<JobKind, JobToast | null> = {
  bulk_contact_creator: {
    actionType: "contact_creator",
    invalidate: (merchantId) => {
      queryClient.invalidateQueries({ queryKey: ["video", merchantId] });
      queryClient.invalidateQueries({ queryKey: ["assurance-video", merchantId] });
    },
  },
  bulk_message_creators: {
    actionType: "message_creator",
    invalidate: (merchantId) => {
      queryClient.invalidateQueries({ queryKey: ["creators_with_activities", merchantId] });
      queryClient.invalidateQueries({ queryKey: ["creator", merchantId] });
      queryClient.invalidateQueries({ queryKey: ["collaborations", merchantId] });
    },
  },
  bulk_import_blocklist: {
    actionType: "import_blocklist",
    invalidate: () => invalidateBlocklist(queryClient),
  },
  insert_sample_application: null,
  classify_candidates: null,
  classify_creators: null,
  generate_creator_filters: null,
  generate_candidate_filters: null,
  update_currency_conversion: null,
  refresh_shop_tokens: null,
};

export const jobToast = (kind: JobKind): JobToast | null => JOB_TOASTS[kind] ?? null;

/** Whether a 202 for this action means a jobs-table job rather than a workflow reporting by event. */
export const isJobBackedAction = (actionType: string): boolean =>
  Object.values(JOB_TOASTS).some((toast) => toast?.actionType === actionType);

export const isTerminalJobStatus = (status: string): boolean =>
  status === "succeeded" || status === "partial" || status === "failed";
